import { ref, reactive } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { cloneDeep, isEmpty, pick } from 'lodash'
import { getRouteData } from '@/modules/PartnerWidget/helpers/tracking'
import { EVENT_OVP } from '@/config/events'
import { events$ } from '@/services'

import { getNewContractStartDate, isInvalidContractStartDate } from '@/modules/PartnerWidget/helpers/dates'
import { BOOLEAN_CONFIG_KEYS, SCREEN } from '@/modules/PartnerWidget/constants'
import { EVENT_LANGUAGE, EVENT_PARTNER_WIDGET } from '@/config/events'
import { CHANNEL, NOTIFICATION, PARTNER_WIDGET_RESTART_TIMEOUT } from '@/config/constants'
import config from '@/config'

import useBackend from '@/hooks/useBackend'
import {
  useBasket,
  useDistribution,
  useError,
  useMasterdata,
  useNotifications,
  useProduct,
  useTranslation,
} from '@/hooks'
import usePartnerWidget from '@/hooks/usePartnerWidget'
import { ReactivityUtil } from '@/utils/Reactivity'

const { updateOvpBasket } = useBasket()
const { doGet, setup } = useBackend()
const { createDistributionPartners } = useDistribution()
const { createErrorResponse } = useError()
const { fetchLocations } = useMasterdata()
const { addNotification } = useNotifications()
const { getCustomThemeCssUrl } = usePartnerWidget()
const { fetchProducts } = useProduct()
const {
  i18n: {
    global: { t },
  },
} = useTranslation()

setup('/partnerwidget', config.microserviceBaseUrl, {
  responseSuccess: response => {
    return response.data
  },
  responseError: error => {
    throw createErrorResponse(error)
  },
})

events$.once(EVENT_LANGUAGE.LOADED, () => {
  languageLoaded.value = true
})

// Data
const bundleId = ref('')
const displayMode = ref('')
const hasChosenPackage = ref(false)
const language = ref('')
const languageLoaded = ref(false)
const ready = ref(false)
const screen = ref(SCREEN.ENTRY)
const screensDefinition = ref({})
const theme = ref('')
const code = ref('')
const bundleConfig = reactive({})
const packages = reactive([])

export default function usePartnerWidgetBundleLoader() {
  function screensDefinitionInit() {
    screensDefinition.value = {
      Entry: {
        events: {
          enter: enter,
        },
        props: {},
      },
      Bundle: {
        events: {
          requireDetail: onDetailRequired,
        },
        props: {
          logo: false,
          packages: [],
          skipEntryPage: false,
        },
      },
      Detail: {
        events: {
          backToBundle: onBackToBundle,
          goToSanitas: goToSanitas,
        },
        props: {
          item: null,
          skipEntryPage: false,
          skipPackagesOverview: false,
          openInSameWindow: false,
        },
      },
      Error: {
        props: {
          messages: [],
          language: language.value,
        },
      },
    }
  }

  async function fetchBundle(__code, __language) {
    const response = await doGet(`/public/bundles/${__code}`, {}, false, { 'Accept-Language': __language })
    return response.data
  }

  async function loadBundle(params) {
    const bundleResponse = await fetchBundle(bundleId.value, language.value)
    code.value = bundleResponse.code
    try {
      ReactivityUtil.reAssign(bundleConfig, overrideBundleWithUrlProps(bundleResponse))
      ReactivityUtil.resetData(packages)

      for (const bundleBasket of bundleResponse.baskets) {
        let location = params.location
        if (location) {
          const contractStartDate = bundleBasket.contractStartDate ?? getNewContractStartDate()
          location = await findLocation(location, contractStartDate)
        }
        let updatedBasketBundle = overrideBasketBundleWithUrlProps(
          cloneDeep(bundleBasket),
          Object.assign({}, params, { location })
        )
        delete updatedBasketBundle.packages
        updatedBasketBundle = await createBasket(bundleBasket.packages, updatedBasketBundle)
        for (const __package of bundleBasket.packages) {
          const productsStructure = await loadProducts({
            contractStartDate: updatedBasketBundle.contractStartDate,
            channel: CHANNEL.PARTNERWIDGET,
            masterTreaty: updatedBasketBundle.masterTreaty,
          })
          const packageData = {
            ...__package,
            basket: updatedBasketBundle,
            productsStructure,
            showExampleOfferText: true,
            isPersonDataEdited: false,
          }
          packages.push(packageData)
        }
      }

      // general
      loadThemeCSS()
      setScreenDefinitions()

      if (bundleConfig.skipEntryPage) {
        enter()
      }
    } catch (e) {
      console.error(e)
      if (e.response) {
        screensDefinition.value.Error.props.messages = e.response.data.infoMessages
      }
      screen.value = SCREEN.ERROR
    }
    ready.value = true
  }

  async function createBasket(__packages, basket) {
    basket.basketId = __packages[0]?.id || uuidv4()

    const allPackageProducts = __packages.reduce((acc, pack) => {
      acc.push(...pack.products)
      return acc
    }, [])

    const payloadBasket = getBasketPayload(basket)
    const __basket = await updateOvpBasket(payloadBasket, { simulate: true })
    return await updateProductsInBasket(__basket, allPackageProducts)
  }

  // find matching location passed via url params
  async function findLocation(params, contractStartDate) {
    const query = params?.zipCode
    const locations = await fetchLocations({ query, contractStartDate }).catch(e => {
      console.warn('location service not available', e)
    })

    const locationData = locations?.data

    if (!locationData || locationData.length === 0) {
      return null
    }

    if (locationData.length === 1) {
      return locationData[0]
    }

    const matchingLocation = locationData.find(location => {
      const locationZipAddon = parseInt(location.zipCodeAddon, 10)
      const locationLocationNumber = parseInt(location.locationNumber, 10)

      const paramZipAddon = parseInt(params.zipCodeAddon, 10)
      const paramLocationNumber = parseInt(params.locationNumber, 10)

      return locationZipAddon === paramZipAddon && locationLocationNumber === paramLocationNumber
    })

    // fall back to first location for zipCode, if zipAddon or locationNumber don't match
    return matchingLocation ?? locationData[0]
  }

  /**
   * Updates the products in the basket (options with accident)
   * If selectProducts is true, the products are selected as well
   */
  function updateProductsInBasket(__basket, products, selectProducts = false, simulate = true) {
    const packageProducts = products
    const updatedBasket = cloneDeep(__basket)
    let personProducts = updatedBasket.persons[0].products.products

    resetProductSelection(personProducts)
    applyCriteriaAndSelectProducts(personProducts, packageProducts, selectProducts)

    return updateOvpBasket(updatedBasket, { simulate })
  }

  function resetProductSelection(personProducts) {
    // reset selection for products
    Object.values(personProducts).forEach(product => {
      product.selected = false
      product.prices.map(price => {
        price.selected = false
        return price
      })
      return product
    })
  }

  function applyCriteriaAndSelectProducts(personProducts, packageProducts, selectProducts) {
    packageProducts.forEach(p => {
      const { id, toggle, value } = p

      // select product
      const basketProduct = personProducts[p.id]
      if (!basketProduct) {
        return
      }

      const priceOptions = basketProduct.prices

      if (selectProducts) {
        basketProduct.selected = true
      }

      const selectedPriceOption = setSelectedPriceOption(priceOptions, value)
      setToggleAndSelectPriceOption(priceOptions, selectedPriceOption, toggle)

      // select product, set value & toggle
      if (selectProducts) {
        // reset selections for priceOptions
        priceOptions.forEach(price => {
          price.selected = false
        })
        if (selectedPriceOption) {
          selectedPriceOption.selected = true
        } else {
          console.error('Can not select correct price', id, toggle, value)
        }
      }
    })
  }

  function setSelectedPriceOption(priceOptions, value) {
    return priceOptions.length > 1
      ? priceOptions.find(price => {
          // check if value defined, value can be undefined for e.g. AB04Produkt
          const isPriceMatching = value ? price.value === parseInt(value, 10) : true
          const isDurationMatching = price.duration ? price.duration === '1' : true
          return isPriceMatching && isDurationMatching
        })
      : priceOptions[0]
  }

  // set toggle value and select this price option. Other toggle values will be set by backend
  function setToggleAndSelectPriceOption(priceOptions, selectedPriceOption, toggle) {
    if (selectedPriceOption && toggle !== undefined) {
      selectedPriceOption.toggle = toggle
      priceOptions.forEach(option => {
        option.selected = false
      })
      selectedPriceOption.selected = true
    }
  }

  function getBasketPayload(bundleBasket) {
    return {
      basketId: bundleBasket.basketId,
      createDate: new Date().toISOString(),
      channel: CHANNEL.PARTNERWIDGET,
      collective: isEmpty(bundleBasket.collective) ? null : bundleBasket.collective,
      masterTreaty: bundleBasket.masterTreaty,
      language: language.value.toUpperCase(),
      partnerWidgetBundleCode: code.value,
      distributionPartners: createDistributionPartners(bundleBasket.distributionPartnerNumber),
      contractStartDate: bundleBasket.contractStartDate,
      address: bundleBasket.address,
      route: 'basket',
      persons: [
        {
          collectivePersonGroup: bundleBasket.collectivePersonGroup,
          personId: uuidv4(),
          personData: {
            dateOfBirth: bundleBasket.person.dateOfBirth,
            firstName: bundleBasket.person.firstName,
            lastName: null,
            gender: bundleBasket.person.gender,
          },
          preInsurer: {},
        },
      ],
    }
  }

  async function loadProducts(__basket) {
    // use basket from backend response, contractStartdate may be set by BE
    return await fetchProducts(__basket).then(products => {
      return products
    })
  }

  function overrideBundleWithUrlProps(__bundle) {
    const { config: __config } = __bundle
    if (theme.value) {
      __config.theme = theme.value
    }
    if (displayMode.value) {
      __config.displayMode = displayMode.value
    }

    // parse boolean __configuration values from url
    BOOLEAN_CONFIG_KEYS.forEach(key => {
      if (__config[key]) {
        __config[key] = __config[key] === 'true' || __config[key]
      }
    })

    return __config
  }

  function overrideBasketBundleWithUrlProps(bundleBasket, __options) {
    // Set current date if bundle contract start date are not valid
    if (bundleBasket.contractStartDate && isInvalidContractStartDate(bundleBasket.contractStartDate)) {
      bundleBasket.contractStartDate = getNewContractStartDate()
    }
    // Set location if valid
    if (!isEmpty(__options.location)) {
      bundleBasket.address = __options.location
    }
    // Set gender if given gender available
    if (['MALE', 'FEMALE', 'UNBORN'].includes(__options.gender)) {
      bundleBasket.person.gender = __options.gender
    }

    if (__options.firstName) {
      bundleBasket.person.firstName = __options.firstName
    }

    // Set dateOfBirth
    if (__options.dateOfBirth) {
      bundleBasket.person.dateOfBirth = __options.dateOfBirth
    }

    return bundleBasket
  }

  function setScreenDefinitions() {
    screensDefinition.value.Entry.props = Object.assign(
      {},
      screensDefinition.value.Entry.props,
      pick(bundleConfig, ['logo', 'badge']),
      { basketAvailable: !!packages[0]?.basket?.basketId }
    )
    screensDefinition.value.Bundle.props = Object.assign(
      {},
      screensDefinition.value.Bundle.props,
      pick(bundleConfig, ['logo', 'skipEntryPage']),
      { packages }
    )
    screensDefinition.value.Detail.props = Object.assign({}, screensDefinition.value.Detail.props, {
      skipEntryPage: bundleConfig.skipEntryPage,
      skipPackagesOverview: bundleConfig.skipPackagesOverview,
      openInSameWindow: bundleConfig.openInSameWindow,
    })
  }

  function addStylesheet(url) {
    const themeFile = document.createElement('link')
    themeFile.setAttribute('rel', 'stylesheet')
    themeFile.setAttribute('type', 'text/css')

    themeFile.setAttribute('href', url)
    window.document.head.appendChild(themeFile)
  }

  function loadThemeCSS() {
    switch (bundleConfig.theme) {
      case 'light': // light theme is default, so doesn't load any css
        break
      case 'darkgreen':
        addStylesheet(`${process.env.VUE_APP_SANITAS_PUBLIC_PATH}partnerWidget/theme-darkgreen.css`)
        break
      case 'custom':
        // never show logo if custom theme is used
        bundleConfig.logo = false

        getCustomThemeCssUrl(code.value, bundleConfig.lastModifiedDate)
          .then(url => {
            addStylesheet(url)
          })
          .catch(e => {
            console.error(e)
          })
        break
      default:
        console.error(`Unknown theme: ${bundleConfig.theme}, falling back to default light theme`)
    }
  }

  function enter() {
    if (bundleConfig.skipPackagesOverview) {
      onDetailRequired(packages[0])
    } else {
      goToBundle()
    }
  }

  function onDetailRequired(pack) {
    screensDefinition.value.Detail.props.item = pack
    screen.value = SCREEN.DETAIL
  }

  function goToBundle() {
    screen.value = SCREEN.BUNDLE
  }

  function onBackToBundle() {
    screen.value = SCREEN.BUNDLE
  }

  async function goToSanitas(options) {
    if (hasChosenPackage.value) {
      return
    }

    events$.emit(EVENT_PARTNER_WIDGET.GOTO_SANITAS)

    try {
      const { item, target } = options
      hasChosenPackage.value = true
      // don't update store! Kvg without doctor can set the valid flag on the product to false,
      // causing the chosen package to be displayed as unavailable!
      let realBasket = cloneDeep(item.basket)
      delete realBasket.createDate
      delete realBasket.basketId
      realBasket = await updateProductsInBasket(realBasket, item.products, true, false)

      const routePayload = getRouteData({
        title: 'PersonSave',
        routes: ['person', 'save'],
      })
      events$.emit(EVENT_OVP.ENTERED, routePayload)

      // navigate to dvp
      const sanitasHref = `${process.env.VUE_APP_SANITAS_PATH}${t('screen.partnerWidget.dvpUrl')}${
        realBasket.reentryToken
      }`
      window.open(sanitasHref, target)

      addNotification(
        t('screen.partnerWidget.popupMessage', { sanitasHref, target }),
        NOTIFICATION.WARNING,
        PARTNER_WIDGET_RESTART_TIMEOUT
      )

      // wait before reloading the widget in case a popup has prevented the new tab from opening
      // due to the navigation, the popup blocker message disappears instantly otherwise.
      window.setTimeout(() => {
        restartWidgetWithData(item.basket)
      }, PARTNER_WIDGET_RESTART_TIMEOUT)
    } catch (e) {
      hasChosenPackage.value = false
      console.error(e)
    }
  }

  function restartWidgetWithData(basket) {
    const parsedUrl = new URL(window.location.href)
    let searchParams = parsedUrl.searchParams

    const { address, persons } = basket
    const firstPersonData = persons[0].personData

    // update search params with entered data
    searchParams.set('gender', firstPersonData.gender)
    searchParams.set('birthDate', firstPersonData.dateOfBirth)

    const { locationNumber, zipCode, zipCodeAddon } = address
    searchParams.set('locationNumber', locationNumber)
    searchParams.set('zipCode', zipCode)
    searchParams.set('zipCodeAddon', zipCodeAddon)

    // restart widget
    location.search = searchParams.toString()
  }

  // Setter methods
  function setBundleId(value) {
    bundleId.value = value
  }

  function setDisplayMode(value) {
    displayMode.value = value
  }

  function setLanguage(value) {
    language.value = value
  }

  function setTheme(value) {
    theme.value = value
  }

  return {
    // Data
    bundleConfig,
    bundleId,
    displayMode,
    hasChosenPackage,
    language,
    languageLoaded,
    ready,
    screen,
    screensDefinition,
    screensDefinitionInit,
    theme,

    // Methods
    loadBundle,

    // Setter Methods
    setBundleId,
    setDisplayMode,
    setLanguage,
    setTheme,
  }
}
